/**
 * Bulk-trade banner (acquisitions only).
 *
 * Postclose loans are frequently purchased in pools: many loans share a
 * `tradeId` and are flagged `bulkTrade`. The backend keeps `relatedLoanIds` +
 * `tradeLoanCount` in sync across the pool (_sync_trade_links), and exposes:
 *   GET  /trade/{tradeId}/loans   -> { tradeId, loanCount, loans[] }
 *   POST /link_trade_loans/{id}   -> recompute linkage, returns the updated deal
 *
 * This surfaces "this loan is 1 of N in trade X", with a roll-up of the pool and
 * one-click navigation to any sibling loan. Renders nothing for a loan that
 * isn't part of a bulk trade.
 */
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import ExpandMore from "@mui/icons-material/ExpandMore";
import LayersOutlined from "@mui/icons-material/LayersOutlined";

import { AgGridReact } from "ag-grid-react";
import AgGridTableWrapper from "../../../components/agGrid/AgGridTableWrapper";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { losAcquisitionsApi } from "../../../api/losAcquisitionsApi";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";
import { usePipeline, useCapability } from "../../../pipelines/PipelineContext";
import formatDollar from "../../../_src_shared/utils/functions/formatDollar";
import { formatPercent } from "../../../utils/numbers/formatPercent";

const AcquisitionsTradeBanner = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const bulkTradeEnabled = useCapability("bulkTrade");
  const { drilldownBase } = usePipeline();
  const loan = useSelector((state) => state.loanDrilldown);

  const [open, setOpen] = useState(false);
  const [loans, setLoans] = useState(null);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);

  const dealId = loan?._id;
  const tradeId = loan?.tradeId;
  const isBulk = !!loan?.bulkTrade && !!tradeId;
  const count = loan?.tradeLoanCount;

  const loadLoans = useCallback(async () => {
    if (!tradeId) return;
    setLoading(true);
    try {
      const res = await losAcquisitionsApi.getTradeLoans(tradeId);
      setLoans(res?.loans || []);
    } catch (e) {
      enqueueSnackbar("Could not load the loans in this trade", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [tradeId, enqueueSnackbar]);

  const handleToggle = useCallback(() => {
    const next = !open;
    setOpen(next);
    if (next && loans === null) loadLoans();
  }, [open, loans, loadLoans]);

  // Recompute relatedLoanIds/tradeLoanCount across the pool — useful when a loan
  // was added to the trade after this one was created.
  const handleRelink = useCallback(async () => {
    if (!dealId) return;
    setLinking(true);
    try {
      const updated = await losAcquisitionsApi.linkTradeLoans(dealId);
      if (updated?._id) dispatch(loanDrilldownSet(updated));
      await loadLoans();
      enqueueSnackbar("Trade linkage recomputed", { variant: "success" });
    } catch (e) {
      enqueueSnackbar("Could not recompute trade linkage", {
        variant: "error",
      });
    } finally {
      setLinking(false);
    }
  }, [dealId, dispatch, loadLoans, enqueueSnackbar]);

  // Pool roll-up across the sibling loans.
  const totals = useMemo(() => {
    if (!loans?.length) return null;
    const upb = loans.reduce((s, l) => s + (l.currentUPB || 0), 0);
    const price = loans.reduce((s, l) => s + (l.purchasePriceAmount || 0), 0);
    // UPB-weighted average note rate — a straight mean would misweight small loans.
    const weighted = loans.reduce(
      (s, l) => s + (l.noteRate || 0) * (l.currentUPB || 0),
      0,
    );
    return {
      upb,
      price,
      wac: upb > 0 ? weighted / upb : null,
      count: loans.length,
    };
  }, [loans]);

  const columnDefs = useMemo(
    () => [
      { field: "loanNumber", headerName: "Loan #", width: 110 },
      { field: "loanName", headerName: "Loan Name", flex: 1, minWidth: 160 },
      { field: "sellerCorrespondentName", headerName: "Seller", flex: 1, minWidth: 140 },
      { field: "loanStatus", headerName: "Status", width: 140 },
      { field: "dueDiligenceStatus", headerName: "Diligence", width: 130 },
      { field: "noteReviewStatus", headerName: "Note Review", width: 130 },
      {
        field: "currentUPB",
        headerName: "Current UPB",
        width: 130,
        valueFormatter: (p) => formatDollar(p.value, "--"),
      },
      {
        field: "purchasePriceAmount",
        headerName: "Purchase Price",
        width: 140,
        valueFormatter: (p) => formatDollar(p.value, "--"),
      },
      {
        field: "noteRate",
        headerName: "Note Rate",
        width: 110,
        valueFormatter: (p) => (p.value == null ? "--" : formatPercent(p.value)),
      },
    ],
    [],
  );

  if (!bulkTradeEnabled || !isBulk) return null;

  return (
    <Box
      sx={{
        border: "1px solid #e3e9ec",
        borderRadius: 1,
        bgcolor: "#f8fafc",
        mb: 2,
      }}
    >
      <Flx jb ac sx={{ px: 2, py: 1.25, gap: 2 }}>
        <Flx ac sx={{ gap: 1.5, minWidth: 0 }}>
          <LayersOutlined sx={{ fontSize: 18, color: "#4338ca" }} />
          <Flx column sx={{ gap: 0.25, minWidth: 0 }}>
            <Txt sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
              Part of a bulk trade
            </Txt>
            <Txt sx={{ fontSize: 12, color: "#64748b" }}>
              Trade {tradeId}
              {count ? ` · ${count} loan${count === 1 ? "" : "s"}` : ""}
            </Txt>
          </Flx>
          <Chip
            size="small"
            label="Bulk Trade"
            sx={{
              fontSize: 11,
              fontWeight: 600,
              bgcolor: "#eef2ff",
              color: "#4338ca",
            }}
          />
        </Flx>
        <Flx ac sx={{ gap: 1 }}>
          <Button
            variant="text"
            size="small"
            disabled={linking}
            onClick={handleRelink}
          >
            {linking ? "Relinking…" : "Recompute Linkage"}
          </Button>
          <Button variant="text" size="small" onClick={handleToggle}>
            {open ? "Hide Trade" : "View Trade"}
          </Button>
          <IconButton size="small" onClick={handleToggle}>
            <ExpandMore
              sx={{
                fontSize: 18,
                transition: "transform 150ms",
                transform: open ? "rotate(180deg)" : "none",
              }}
            />
          </IconButton>
        </Flx>
      </Flx>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ borderTop: "1px solid #e3e9ec", p: 2 }}>
          {loading ? (
            <Flx ac jc sx={{ py: 3 }}>
              <CircularProgress size={20} />
            </Flx>
          ) : (
            <Flx column sx={{ gap: 2 }}>
              {totals && (
                <Flx sx={{ gap: 4, flexWrap: "wrap" }}>
                  {[
                    { label: "Loans", value: totals.count },
                    { label: "Total UPB", value: formatDollar(totals.upb, "--") },
                    {
                      label: "Total Purchase Price",
                      value: formatDollar(totals.price, "--"),
                    },
                    {
                      label: "WAC (UPB-weighted)",
                      value: totals.wac == null ? "--" : formatPercent(totals.wac),
                    },
                  ].map((s) => (
                    <Flx column key={s.label} sx={{ gap: 0.25 }}>
                      <Txt
                        sx={{
                          fontSize: 10.5,
                          fontWeight: 700,
                          letterSpacing: 0.5,
                          color: "#94a3b8",
                        }}
                      >
                        {s.label.toUpperCase()}
                      </Txt>
                      <Txt
                        sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}
                      >
                        {s.value}
                      </Txt>
                    </Flx>
                  ))}
                </Flx>
              )}

              <AgGridTableWrapper height="260px">
                <AgGridReact
                  rowData={loans || []}
                  columnDefs={columnDefs}
                  getRowId={(p) => String(p.data._id)}
                  onRowClicked={(p) => {
                    if (p.data?._id && String(p.data._id) !== String(dealId)) {
                      navigate(`${drilldownBase}/${p.data._id}`);
                    }
                  }}
                  rowStyle={{ cursor: "pointer" }}
                />
              </AgGridTableWrapper>
            </Flx>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default AcquisitionsTradeBanner;
